import React from "react";
import LocationOnIcon from "@mui/icons-material/LocationOn";

/* ================= Types ================= */
interface CityOption {
  city: { id: number; name: string };
  delivery_fee: number;
  estimated_days: number;
}

interface Props {
  storeId: number;
  cityOptions: CityOption[];
  selectedCity: CityOption | null;
  isRTL: boolean;
  t: (key: string) => string;
  onSelectCity: (cityOption: CityOption) => void;
  currencySymbol?: string;
}

/* ================= Component ================= */
const ResponsiveQuickSelectionTabs: React.FC<Props> = ({
  storeId,
  cityOptions,
  selectedCity,
  isRTL,
  t,
  onSelectCity,
  currencySymbol = "ج.م",
}) => {
  const compactCities = cityOptions.slice(0, 4);
  const defaultCities = cityOptions.slice(0, 6);

  /* ================= Compact (Mobile) ================= */
  const CompactView = () => (
    <div className="mt-4">
      <p className="text-xs font-medium text-gray-600 mb-2">
        {t("cart.quickSelect") || "اختيار سريع"}
      </p>

      <div className="flex flex-wrap gap-2">
        {compactCities.map((opt) => {
          const active = selectedCity?.city.id === opt.city.id;

          return (
            <button
              key={`c-${storeId}-${opt.city.id}`}
              onClick={() => onSelectCity(opt)}
              className={`px-3 py-2 rounded-lg border transition
                ${active
                  ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                  : "border-gray-300 bg-gray-50 hover:border-emerald-400"
                }`}
            >
              <div className="flex items-center gap-1">
                <LocationOnIcon className="text-sm" />
                <span className="text-sm truncate max-w-[80px]">
                  {opt.city.name}
                </span>
              </div>
              <div className="text-xs mt-1 font-medium">
                {opt.delivery_fee} {currencySymbol}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================= Default (Tablet) ================= */
  const DefaultView = () => (
    <div className="mt-6">
      <p className="text-sm font-medium text-gray-700 mb-3">
        {t("cart.recommendedCities") || "المدن الموصى بها"}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {defaultCities.map((opt) => {
          const active = selectedCity?.city.id === opt.city.id;

          return (
            <button
              key={`d-${storeId}-${opt.city.id}`}
              onClick={() => onSelectCity(opt)}
              className={`p-3 rounded-xl border-2 transition
                ${active
                  ? "border-emerald-500 bg-emerald-50 shadow-md"
                  : "border-gray-200 hover:border-emerald-300 bg-white"
                }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <LocationOnIcon
                  className={active ? "text-emerald-600" : "text-gray-400"}
                />
                <span className="font-semibold truncate">
                  {opt.city.name}
                </span>
              </div>

              <div className="flex justify-between text-sm">
                <span>
                  {opt.estimated_days} {t("cart.days") || "أيام"}
                </span>
                <span className="font-bold text-emerald-700">
                  {opt.delivery_fee.toLocaleString()} {currencySymbol}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================= Premium (Desktop) ================= */
  const PremiumView = () => (
    <div className="mt-6">
      <div
        className={`flex items-center justify-between mb-4 ${
          isRTL ? "flex-row-reverse" : ""
        }`}
      >
        <h3 className="font-semibold text-gray-900">
          {t("cart.popularCities") || "المدن الأكثر طلباً"}
        </h3>
        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
          {defaultCities.length} {t("cart.cities") || "مدينة"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {defaultCities.map((opt, index) => {
          const active = selectedCity?.city.id === opt.city.id;

          return (
            <button
              key={`p-${storeId}-${opt.city.id}`}
              onClick={() => onSelectCity(opt)}
              className={`p-4 rounded-xl border transition
                ${active
                  ? "border-emerald-500 bg-emerald-50 shadow-lg"
                  : "border-gray-200 hover:border-emerald-400 bg-white"
                }`}
            >
              <div className="flex justify-between mb-2">
                <h4 className="font-bold">{opt.city.name}</h4>
                <span className="text-xs text-gray-400">#{index + 1}</span>
              </div>

              <div className="text-sm flex justify-between">
                <span>
                  {opt.estimated_days} {t("cart.days") || "يوم"}
                </span>
                <span className="font-bold text-emerald-700">
                  {opt.delivery_fee.toLocaleString()} {currencySymbol}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  /* ================= Render ================= */
  return (
    <>
      {/* Mobile */}
      <div className="block sm:hidden">
        <CompactView />
      </div>

      {/* Tablet */}
      <div className="hidden sm:block lg:hidden">
        <DefaultView />
      </div>

      {/* Desktop */}
      <div className="hidden lg:block">
        <PremiumView />
      </div>
    </>
  );
};

export default ResponsiveQuickSelectionTabs;
